import React, { useMemo } from 'react'
import { Checkbox, Form, InputNumber, Radio, Spin, Switch, Table } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { getMemberLevelList, loadMemberSettingConfig, saveMemberSettingConfig } from '../api'
import SettingPage, {
    MONTH_OPTIONS,
    SettingActions,
    SettingGrid,
    SettingInputNumber,
    SettingPanel,
    SettingPreview,
    SettingTableContainer,
    WEEK_OPTIONS,
    statusToBool,
} from './setting-page'
import { validatePointBoosterLevels, validatePointDeductionValues } from './points-setting-utils'
import { savePointSettings } from './points-setting-save'
import { useMemberSettingAccess } from './setting-access'

interface PointBoosterLevel {
    member_level_id?: number | null
    level?: number | null
    name: string
    multiplier?: number | null
    origin_multiplier?: number | null
    is_svip?: boolean
}

interface PointSettingForm {
    point_enable: boolean
    redemption_ratio?: number
    mall_redemption_ratio?: number
    rules_list?: number[]
    redemption_range_list?: number[]
    expiration?: number
    deduction_enable: boolean
    deduction_frequency: number
    deduction_weekdays?: number[]
    deduction_monthdays?: number[]
    money?: number
    origin_money?: number
    high_precision_money?: number
    percentage_ceiling?: number
    mall_percentage_ceiling?: number
    base_amount?: number
    is_default_used: boolean
    levels: PointBoosterLevel[]
}

interface PointSettingData {
    point_get_config?: any
    point_booster_config?: any
    point_deduction_config?: any
    member_levels?: any[]
    svip_config?: any
}

const ruleOptions = [
    { label: '改价单品不积分', value: 0 },
    { label: '整单打折订单不积分', value: 1 },
    { label: '会员日折扣订单不积分', value: 2 },
    { label: '会员价商品不积分', value: 3 },
    { label: '余额支付金额不积分', value: 4 },
    { label: '抹零订单不积分', value: 5 },
    { label: '减免订单不积分', value: 6 },
]

const rangeOptions = [
    { label: '收银台', value: 0 },
    { label: '商城', value: 1 },
]

const expirationOptions = [
    { label: '永久有效', value: 0 },
    { label: '一年', value: 1 },
    { label: '半年', value: 2 },
]

const getLevelKey = (level: Pick<PointBoosterLevel, 'member_level_id' | 'level' | 'is_svip'>) => {
    if (level.is_svip) return 'svip'
    if (level.member_level_id) return `id-${level.member_level_id}`

    return `level-${level.level}`
}

const toPointBoosterLevels = (data: PointSettingData): PointBoosterLevel[] => {
    const configuredLevels = ((data.point_booster_config || {}).levels || []).map((item: any) => ({
        member_level_id: item.member_level?.id || null,
        level: item.member_level?.level ?? null,
        name: item.is_svip ? '付费会员' : item.member_level?.name || '未命名等级',
        multiplier: item.multiplier === null || item.multiplier === undefined ? null : Number(item.multiplier),
        origin_multiplier: item.multiplier === null || item.multiplier === undefined ? null : Number(item.multiplier),
        is_svip: item.is_svip,
    }))
    const configuredMap = new Map<string, PointBoosterLevel>(
        configuredLevels.map((level: PointBoosterLevel) => [getLevelKey(level), level]),
    )
    const memberLevels: PointBoosterLevel[] = (data.member_levels || []).map(level => {
        const item: PointBoosterLevel = {
            member_level_id: level.id,
            level: level.level,
            name: level.name || '未命名等级',
            multiplier: null,
        }

        return configuredMap.get(getLevelKey(item)) || item
    })
    if (data.svip_config?.status) {
        const svipLevel = { member_level_id: null, level: null, name: '付费会员', multiplier: null, is_svip: true }
        memberLevels.push(configuredMap.get(getLevelKey(svipLevel)) || svipLevel)
    }

    return memberLevels
}

const PointsSetting: React.FC = () => {
    const [form] = Form.useForm<PointSettingForm>()
    const access = useMemberSettingAccess()

    const loadReq = useRequest(
        async () => {
            const [config, memberLevels, svipConfig] = await Promise.all([
                loadMemberSettingConfig('/activity/getpointactivityinfo'),
                getMemberLevelList(),
                loadMemberSettingConfig('/membership/getsvipconfigadmin'),
            ])

            return {
                ...config,
                member_levels: memberLevels || [],
                svip_config: svipConfig || {},
            }
        },
        {
            onSuccess: res => {
                const pointConfig = res?.point_get_config || {}
                const deductionConfig = res?.point_deduction_config || {}
                form.setFieldsValue({
                    point_enable: statusToBool(pointConfig.status),
                    redemption_ratio: Number(pointConfig.redemption_ratio || 1),
                    mall_redemption_ratio: Number(pointConfig.mall_redemption_ratio || 1),
                    rules_list: pointConfig.restrictions_list || [],
                    redemption_range_list: pointConfig.redemption_range_list || [0, 1],
                    expiration: pointConfig.expiration ?? 0,
                    deduction_enable: statusToBool(deductionConfig.status),
                    deduction_frequency: deductionConfig.frequency || 0,
                    deduction_weekdays: deductionConfig.weekdays || [],
                    deduction_monthdays: deductionConfig.monthdays || [],
                    money: Number(deductionConfig.money || 0),
                    origin_money: Number(deductionConfig.money || 0),
                    high_precision_money: Number(deductionConfig.high_precision_money || deductionConfig.money || 0),
                    percentage_ceiling: Number(deductionConfig.percentage_ceiling || 0),
                    mall_percentage_ceiling: Number(deductionConfig.mall_percentage_ceiling || 0),
                    base_amount: Number(deductionConfig.base_amount || 100),
                    is_default_used: !!deductionConfig.is_default_used,
                    levels: toPointBoosterLevels(res || {}),
                })
            },
            onError: e => $msg('error', e.message || '获取积分设置失败'),
        },
    )

    const saveReq = useRequest(
        (values: PointSettingForm) => savePointSettings(values, loadReq.data, saveMemberSettingConfig),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '保存成功')
                loadReq.refresh()
            },
            onError: e => {
                $msg('error', e.message || '保存积分设置失败')
                loadReq.refresh()
            },
        },
    )

    const columns = useMemo(
        () => [
            {
                title: '会员等级',
                dataIndex: 'name',
                render: (value: string, __: any, index: number) => (
                    <>
                        <Form.Item name={['levels', index, 'member_level_id']} hidden>
                            <InputNumber />
                        </Form.Item>
                        <Form.Item name={['levels', index, 'level']} hidden>
                            <InputNumber />
                        </Form.Item>
                        <Form.Item name={['levels', index, 'is_svip']} hidden valuePropName="checked">
                            <Checkbox />
                        </Form.Item>
                        <Form.Item name={['levels', index, 'origin_multiplier']} hidden>
                            <InputNumber />
                        </Form.Item>
                        {value}
                    </>
                ),
            },
            {
                title: '积分倍数',
                dataIndex: 'multiplier',
                render: (_: any, __: any, index: number) => (
                    <Form.Item name={['levels', index, 'multiplier']} noStyle>
                        <InputNumber min={1} max={10} precision={1} placeholder="不设置" />
                    </Form.Item>
                ),
            },
        ],
        [],
    )

    const onSubmit = async () => {
        const values = await form.validateFields()
        if (values.point_enable && !(values.redemption_range_list || []).length) {
            $msg('warning', '请选择积分场景')

            return
        }
        if (values.deduction_enable) {
            const activeDays =
                values.deduction_frequency === 1 ? values.deduction_monthdays || [] : values.deduction_weekdays || []
            if (!activeDays.length) {
                $msg('warning', '请选择积分抵现生效日期')

                return
            }
            const pointDeductionError = validatePointDeductionValues(values)
            if (pointDeductionError) {
                $msg('warning', pointDeductionError)

                return
            }
        }
        const pointBoosterError = validatePointBoosterLevels(values.levels || [])
        if (pointBoosterError) {
            $msg('warning', pointBoosterError)

            return
        }
        saveReq.run(values)
    }

    return (
        <SettingPage title="积分设置" readOnly={access.readOnly}>
            <Spin spinning={loadReq.loading}>
                <Form form={form} layout="vertical">
                    <SettingGrid>
                        <SettingPanel
                            title="积分获取规则"
                            desc="设置消费金额转换积分的比例、适用场景和不参与积分的订单类型。"
                        >
                            <Form.Item noStyle shouldUpdate>
                                {() => (
                                    <SettingPreview
                                        items={[
                                            {
                                                label: '收银台积分',
                                                value: `1 元 = ${form.getFieldValue('redemption_ratio') ?? '--'} 积分`,
                                            },
                                            {
                                                label: '商城积分',
                                                value: `1 元 = ${
                                                    form.getFieldValue('mall_redemption_ratio') ?? '--'
                                                } 积分`,
                                            },
                                        ]}
                                    />
                                )}
                            </Form.Item>
                            <Form.Item name="point_enable" label="积分状态" valuePropName="checked">
                                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                            </Form.Item>
                            <Form.Item name="redemption_ratio" label="收银台积分比例">
                                <SettingInputNumber min={0} precision={0} addonBefore="1 元得" addonAfter="积分" />
                            </Form.Item>
                            <Form.Item name="mall_redemption_ratio" label="商城积分比例">
                                <SettingInputNumber min={0} precision={0} addonBefore="1 元得" addonAfter="积分" />
                            </Form.Item>
                            <Form.Item name="redemption_range_list" label="积分场景">
                                <Checkbox.Group options={rangeOptions} />
                            </Form.Item>
                            <Form.Item name="rules_list" label="不参与积分">
                                <Checkbox.Group options={ruleOptions} />
                            </Form.Item>
                            <Form.Item name="expiration" label="积分有效期">
                                <Radio.Group options={expirationOptions} />
                            </Form.Item>
                        </SettingPanel>

                        <SettingPanel title="积分抵现" desc="设置会员下单时使用积分抵扣金额的比例和单笔上限。">
                            <Form.Item noStyle shouldUpdate>
                                {() => (
                                    <SettingPreview
                                        items={[
                                            {
                                                label: '抵现比例',
                                                value: `100 积分 = ${form.getFieldValue('money') ?? '--'} 元`,
                                            },
                                            {
                                                label: '收银台上限',
                                                value: `${
                                                    Number(form.getFieldValue('percentage_ceiling') || 0) * 100
                                                }%`,
                                            },
                                            {
                                                label: '商城上限',
                                                value: `${
                                                    Number(form.getFieldValue('mall_percentage_ceiling') || 0) * 100
                                                }%`,
                                            },
                                        ]}
                                    />
                                )}
                            </Form.Item>
                            <Form.Item name="deduction_enable" label="抵现状态" valuePropName="checked">
                                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                            </Form.Item>
                            <Form.Item name="money" label="抵现比例">
                                <SettingInputNumber
                                    min={0}
                                    max={100}
                                    precision={1}
                                    addonBefore="100 积分抵"
                                    addonAfter="元"
                                />
                            </Form.Item>
                            <Form.Item name="origin_money" hidden>
                                <InputNumber />
                            </Form.Item>
                            <Form.Item name="high_precision_money" hidden>
                                <InputNumber />
                            </Form.Item>
                            <Form.Item name="base_amount" label="抵扣基数">
                                <SettingInputNumber min={1} precision={0} addonAfter="积分" />
                            </Form.Item>
                            <Form.Item name="percentage_ceiling" label="收银台单笔抵扣上限">
                                <SettingInputNumber min={0} max={1} precision={4} addonAfter="订单比例" />
                            </Form.Item>
                            <Form.Item name="mall_percentage_ceiling" label="商城单笔抵扣上限">
                                <SettingInputNumber min={0} max={1} precision={4} addonAfter="订单比例" />
                            </Form.Item>
                            <Form.Item name="is_default_used" label="默认使用积分抵现" valuePropName="checked">
                                <Switch checkedChildren="是" unCheckedChildren="否" />
                            </Form.Item>
                            <Form.Item name="deduction_frequency" label="抵现生效周期">
                                <Radio.Group>
                                    <Radio value={0}>每周</Radio>
                                    <Radio value={1}>每月</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, next) => prev.deduction_frequency !== next.deduction_frequency}
                            >
                                {() =>
                                    form.getFieldValue('deduction_frequency') === 1 ? (
                                        <Form.Item name="deduction_monthdays" label="每月生效日期">
                                            <Checkbox.Group options={MONTH_OPTIONS} />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item name="deduction_weekdays" label="每周生效日期">
                                            <Checkbox.Group options={WEEK_OPTIONS} />
                                        </Form.Item>
                                    )
                                }
                            </Form.Item>
                        </SettingPanel>

                        <SettingPanel title="多倍积分" desc="设置会员等级对应的积分倍数。" wide>
                            <Form.Item noStyle shouldUpdate>
                                {() => (
                                    <SettingTableContainer>
                                        <Table
                                            bordered
                                            rowKey={(_, index) => String(index)}
                                            columns={columns}
                                            dataSource={form.getFieldValue('levels') || []}
                                            pagination={false}
                                        />
                                    </SettingTableContainer>
                                )}
                            </Form.Item>
                            <SettingActions>
                                <Auth perssion={20} onClick={onSubmit}>
                                    <Button types="default" loading={saveReq.loading} disabled={saveReq.loading}>
                                        保存
                                    </Button>
                                </Auth>
                            </SettingActions>
                        </SettingPanel>
                    </SettingGrid>
                </Form>
            </Spin>
        </SettingPage>
    )
}

export default PointsSetting
